import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {DiscModalViewerComponent} from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import {DfMeDropdownComponent} from '../../../shared/components/df-me-dropdown/df-me-dropdown.component';
import {CreateDisclosureService, DSTTaxYear} from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import {DSTStatement} from '../../shared/models/dst-statement.model';
import { Subscription, take, zip } from 'rxjs';
import {DSTSharedService} from '../../shared/dst-shared.service';
import {StmtIdBtnService} from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import {TrackerTabsService} from '../../statement-tracker/tracker-tabs.service';
import {DSTDynamicComponentResolverService} from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import {JSONExportService} from '../../../shared/_services/JSONExport.service';
import {ReportsService} from '../reports.service';
import { HttpErrorResponse } from '@angular/common/http';
import {DSTChildFormState} from '../../shared/models/dst-child-form-state.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import { buildDisplayedColumns, connectMatTableSort, getDstColStyle } from '../../shared/mat-table/dst-mat-grid.util';


import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-form8594-report',
    templateUrl: './form-8594-report.component.html',
    styleUrls: ['../../shared/styles/dst-shared.scss', '../../shared/mat-table/dst-mat-table.scss', './form-8594-report.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Form8594ReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  private contextApplied = false;

  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;
  @ViewChild(MatSort) matSort!: MatSort;

  data: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  cols: any[] = [
    {
      field: 'STATEMENTID',
      header: 'Statement Id',
      hidden: false,
      width: '145px'
    },
    {
      field: 'PROJECT_NAME',
      header: 'Project',
      hidden: false,
      width: '105px'
    },
    {
      field: 'PRIMARYLEID', //
      header: 'Purchaser or Seller LEID',
      hidden: false,
      width: '120px'
    },
    {
      field: 'PRIMARYENTITY', //
      header: 'Purchaser or Seller Name',
      hidden: false,
      width: '180px'
    },
    {
      field: 'PURCHASERORSELLER_VAL', //
      header: 'Indicate if Purchaser or Seller',
      hidden: false,
      width: '120px'
    },
    {
      field: 'FILING_GROUP',
      header: 'Filing Group',
      hidden: false,
      width: '145px'
    },
    {
      field: 'SECONDARYLEID', //
      header: 'Other Party Name',
      hidden: false,
      width: '175px'
    },
    {
      field: 'EINOFOTHERPARTY1_VAL', //
      header: 'Other Party EIN',
      hidden: false,
      width: '180px'
    },
    {
      field: 'EIN_VAL', //
      header: 'Other Party Missing EIN Reason',
      hidden: false,
      width: '170px'
    },
    {
      field: 'ADDRESSOFOTHERPARTY1_VAL', //
      header: 'Address Line 1',
      hidden: false,
      width: '275px'
    },
    {
      field: 'ADDRESSOFOTHERPARTY2_VAL', //
      header: 'Address Line 2',
      hidden: false,
      width: '236px'
    },
    {
      field: 'CITYOFOTHERPARTY_VAL', //
      header: 'City',
      hidden: false,
      width: '200px'
    },
    {
      field: 'STATE_US_VAL', //
      header: 'US State (if applicable)',
      hidden: false,
      width: '155px'
    },
    {
      field: 'COUNTRY1_VAL', //
      header: 'Country Code',
      hidden: false,
      width: '155px'
    },
    {
      field: 'ZIPCODE5_VAL', //
      header: 'Postal Code',
      hidden: false,
      width: '155px'
    },
    {
      field: 'DATEOFSALE_VAL', //
      header: 'Sales Date',
      hidden: false,
      width: '125px'
    },
    {
      field: 'TOTALSALESPRICE_VAL', //
      header: 'Sales Price',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS1FMV_VAL', //
      header: 'FMV Class I',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS2FMV_VAL', //
      header: 'FMV Class II',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS3FMV_VAL', //
      header: 'FMV Class III',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS4FMV_VAL', //
      header: 'FMV Class IV',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS5FMV_VAL', //
      header: 'FMV Class V',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS6N7FMV_VAL', //
      header: 'FMV Class VI & VII',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'TOTALFMV_VAL', //
      header: 'Total FMV',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS1ASP_VAL', //
      header: 'Allocation Class I',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS2ASP_VAL', //
      header: 'Allocation Class II',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS3ASP_VAL', //
      header: 'Allocation Class III',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS4ASP_VAL', //
      header: 'Allocation Class IV',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS5ASP_VAL', //
      header: 'Allocation Class V',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS6N7ASP_VAL', //
      header: 'Allocation Class VI & VII',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'TOTALASP_VAL', //
      header: 'Total Allocation',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CHOICE204A_VAL', //
      header: 'Question 5: Sales Contract or Written Document (Y/N)',
      hidden: false,
      width: '170px'
    },
    {
      field: 'CHOICE204B_VAL', //
      header: 'If Yes, Aggregate FMV specified agrees to Sales Contract? (Y/N)',
      hidden: false,
      width: '200px'
    },
    {
      field: 'CHOICE204C_VAL', //
      header: 'Question 6: Purchaser also purchase license/convenant not to compete, or enter into lease agreement, employment contract, etc with seller? (Y/N)',
      hidden: false,
      width: '300px'
    },

    {
      field: 'TAXYEAR1_VAL', 
      header: 'Tax year with the original Form 8594 and any supplemental statements were filed.',
      hidden: false,
      width: '150px'
    },
    {
      field: 'FORM1_VAL', 
      header: 'Tax return form number with the original Form 8594 and any supplemental statements were filed.',
      hidden: false,
      width: '150px'
    },

    {
      field: 'CLASS1PPR_VAL', 
      header: 'Allocation of sales price Class I',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS2PPR_VAL', 
      header: 'Allocation of sales price Class II',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS3PPR_VAL', 
      header: 'Allocation of sales price Class III',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS4PPR_VAL', 
      header: 'Allocation of sales price Class IV',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS5PPR_VAL', 
      header: 'Allocation of sales price Class V',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS6N7PPR_VAL', 
      header: 'Allocation of sales price Class VI and VII',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'TOTALPPR_VAL', 
      header: 'Total Allocation of Sales Price',
      hidden: false,
      width: '170px',
      format: 'number'
    },

    {
      field: 'CLASS1INCDEC_VAL', 
      header: 'Increase or (decrease) Class I',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS2INCDEC_VAL', 
      header: 'Increase or (decrease) Class II',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS3INCDEC_VAL', 
      header: 'Increase or (decrease) Class III',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS4INCDEC_VAL', 
      header: 'Increase or (decrease) Class IV',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS5INCDEC_VAL', 
      header: 'Increase or (decrease) Class V',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS6N7INCDEC_VAL', 
      header: 'Increase or (decrease) Class VI and VII',
      hidden: false,
      width: '170px',
      format: 'number'
    },

    {
      field: 'CLASS1RASP_VAL', 
      header: 'Redetermination of allocation of sales price Class I',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS2RASP_VAL', 
      header: 'Redetermination of allocation of sales price Class II',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS3RASP_VAL', 
      header: 'Redetermination of allocation of sales price Class III',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS4RASP_VAL', 
      header: 'Redetermination of allocation of sales price Class IV',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS5RASP_VAL', 
      header: 'Redetermination of allocation of sales price Class V',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'CLASS6N7RASP_VAL', 
      header: 'Redetermination of allocation of sales price Class VI and VII',
      hidden: false,
      width: '170px',
      format: 'number'
    },
    {
      field: 'TOTALRASP_VAL', 
      header: 'Total Redetermination of allocation of sales price',
      hidden: false,
      width: '170px',
      format: 'number'
    },

    {
      field: 'REASONINCDEC_VAL', //
      header: 'Question 9: Reason(s) for increase or decrease.',
      hidden: false,
      width: '300px'
    }

  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;

  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;
  selectedMes: string = '';
  activeRow!: DSTStatement;
  showModalViewer: boolean = false;
  private onDisCancelSubs!: Subscription;

  constructor(private cDSerivce: CreateDisclosureService,
              private dstSharedService: DSTSharedService,
              private stmtIdBtnService: StmtIdBtnService,
              private trackerTabsService: TrackerTabsService,
              private dCRService: DSTDynamicComponentResolverService,
              private jsonExportService: JSONExportService,
              private reportsService: ReportsService,
              private gtwGlobal: GlobalService,
              private gtwWorkspace: WorkspaceService,
              private gtwApp: AppService) {

    this.excelExportFileName = 'Reports-8594_Report_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = buildDisplayedColumns(this.cols);

    // local test only
    // this.dstSharedService.setBaseURLs({
    //   api: '/gtw',
    //   custom: '/custom',
    //   admin: '/admin',
    //   efile: '/gtw-efile-api',
    //   pdfEngine: '/pdf-engine',
    //   bulkPdfEngine: '/bulk-pdf-engine'
    // });
    // this.dstSharedService.setClientKey('0');
    // this.dstSharedService.setDSTUser('400394467', 2);
    // this.dstSharedService.setS3uploadButtonAccessUsers(['400394467']);
    // this.dstSharedService.setTaxYearScenario('2020', '40');
  }

  ngOnInit(): void {
    // this.dstSharedService.setDSTUser('400394467', 5);
    // local test only
    // const getTaxYears$ = this.cDSerivce.getTaxYears();
    // zip(getTaxYears$).pipe(take(1)).subscribe((data) => {
    //   this.taxYears = data[0];
    //   this.setTaxYear('2020');
    //   this.loadReport();
    // });
    // end of local test only
    this.onDisCancelSubs = this.dCRService.onDisclosureCancel.subscribe(() => {
      this.trackerTabsService.destroyModalTab();
      this.showModalViewer = false;
    });

    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    connectMatTableSort(this.dataSource, this.matSort);
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  getColStyle(col: { width?: string }): Record<string, string> {
    return getDstColStyle(col);
  }

  onMatSortChange(): void {
    connectMatTableSort(this.dataSource, this.matSort);
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;
      this.contextApplied = true;

      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);

      const getTaxYears$ = this.cDSerivce.getTaxYears();
      zip(getTaxYears$).pipe(take(1)).subscribe((data) => {
        this.taxYears = data[0];
        this.setTaxYear(taxYear);
        // this.setScenarios(param.taxYear);
        this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
        this.loadReport();
      });
    } catch (e) {
      this.dstSharedService.showAlert('error', 'Missing context, please contact system administrator for assistance!');
    }
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  selectedMeChanged(selected: string): void {
    this.selectedMes = selected;
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    console.log('Selected M/Es:- ' + this.selectedMes);
    this.reportsService.loadForm8594Report(this.taxYear?.text.toString()!, this.selectedMes || '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.data = data;
      this.data?.forEach((element) => { //params for opening statement
        element.statementId = element.STATEMENTID;
        element.taxYearId = element.TAXYEARID;
        element.formId = element.FORMID;
        element.formName = element.FORMNAME;
      });
      this.dataSource.data = this.data;
      connectMatTableSort(this.dataSource, this.matSort);
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.data = [];
      this.dataSource.data = [];
      this.dstSharedService.showAlert('error', 'Something went wrong while loading the 8594 Report grid data, please try again!');
    }).add(() => {
      this.loading = false;
    });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.dstSharedService.setTableActionStatus(true);
    this.stmtIdBtnService.getChildStatements(rowData.STATEMENTID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
      this.showModalViewer = true;
      setTimeout(() => {
        this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
      });
    }, (eMsg: string) => {
      console.log(eMsg);
      this.dstSharedService.showAlert('error', eMsg);
    }).add(() => {
      this.dstSharedService.setTableActionStatus(false);
    });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  exportToExcel(): void {
    this.jsonExportService.exportToExcel(this.excelExportFileName, this.data, this.cols);
  }
}
